import React from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { AnimatedContainer } from '@/components/transitions';
import {
  Inbox,
  Search,
  Lock,
  AlertCircle,
  FileX,
  Users,
  Plus,
  Upload,
  FolderOpen,
  MessageSquare,
  Bell,
  Cloud,
  Lightbulb,
  type LucideIcon,
} from 'lucide-react';

export interface EmptyStateProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Icon to display (Lucide icon component) */
  icon?: LucideIcon;
  /** Icon size */
  iconSize?: 'sm' | 'md' | 'lg';
  /** Title text */
  title: string;
  /** Description text */
  description?: string;
  /** Primary action button */
  primaryAction?: {
    label: string;
    onClick: () => void;
    icon?: LucideIcon;
  };
  /** Secondary action link */
  secondaryAction?: {
    label: string;
    onClick: () => void;
  };
  /** Size variant */
  size?: 'sm' | 'md' | 'lg';
  /** Optional illustration image */
  illustration?: string;
  /** Preset empty state type */
  type?: 'no-data' | 'no-results' | 'no-permissions' | 'error' | 'custom';
}

const sizeClasses = {
  sm: {
    container: 'py-8 px-4',
    icon: 'w-8 h-8',
    title: 'text-base',
    description: 'text-sm max-w-xs',
    button: 'px-3 py-1.5 text-sm',
  },
  md: {
    container: 'py-12 px-6',
    icon: 'w-12 h-12',
    title: 'text-lg',
    description: 'text-base max-w-md',
    button: 'px-4 py-2 text-base',
  },
  lg: {
    container: 'py-16 px-8',
    icon: 'w-16 h-16',
    title: 'text-xl',
    description: 'text-base max-w-lg',
    button: 'px-6 py-3 text-base',
  },
};

const presetConfigs = {
  'no-data': {
    icon: Inbox,
    defaultTitleKey: 'emptyStates.presets.noData.title',
    defaultDescriptionKey: 'emptyStates.presets.noData.description',
    iconColor: 'text-fg-muted',
  },
  'no-results': {
    icon: Search,
    defaultTitleKey: 'emptyStates.presets.noResults.title',
    defaultDescriptionKey: 'emptyStates.presets.noResults.description',
    iconColor: 'text-fg-muted',
  },
  'no-permissions': {
    icon: Lock,
    defaultTitleKey: 'emptyStates.presets.noPermissions.title',
    defaultDescriptionKey: 'emptyStates.presets.noPermissions.description',
    iconColor: 'text-warning',
  },
  error: {
    icon: AlertCircle,
    defaultTitleKey: 'emptyStates.presets.error.title',
    defaultDescriptionKey: 'emptyStates.presets.error.description',
    iconColor: 'text-danger',
  },
  custom: {
    icon: undefined,
    defaultTitleKey: '',
    defaultDescriptionKey: '',
    iconColor: 'text-fg-muted',
  },
};

export function EmptyState({
  icon: CustomIcon,
  iconSize = 'md',
  title,
  description,
  primaryAction,
  secondaryAction,
  size = 'md',
  illustration,
  type = 'custom',
  className,
  ...props
}: EmptyStateProps) {
  const { t } = useTranslation();
  const config = presetConfigs[type];
  const Icon = CustomIcon || config.icon;
  const finalTitle = title || (config.defaultTitleKey ? t(config.defaultTitleKey) : '');
  const finalDescription =
    description ||
    (type !== 'custom' && config.defaultDescriptionKey
      ? t(config.defaultDescriptionKey)
      : undefined);
  const sizeConfig = sizeClasses[size];

  // Map icon size to actual size based on component size
  const iconSizeMap = {
    sm: size === 'sm' ? 'w-6 h-6' : size === 'md' ? 'w-8 h-8' : 'w-10 h-10',
    md: size === 'sm' ? 'w-8 h-8' : size === 'md' ? 'w-12 h-12' : 'w-14 h-14',
    lg: size === 'sm' ? 'w-10 h-10' : size === 'md' ? 'w-14 h-14' : 'w-16 h-16',
  };

  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center text-center',
        sizeConfig.container,
        className
      )}
      {...props}
    >
      {/* Icon or Illustration */}
      <AnimatedContainer direction="none" duration="fast">
        {illustration ? (
          <img
            src={illustration}
            alt=""
            className={cn(
              'mb-4 opacity-80',
              size === 'sm' && 'max-w-[120px]',
              size === 'md' && 'max-w-[180px]',
              size === 'lg' && 'max-w-[240px]'
            )}
          />
        ) : Icon ? (
          <div
            className={cn(
              'mb-4 p-3 rounded-full',
              'bg-bg-surface border border-border',
              type === 'error' && 'border-danger bg-danger-dim',
              type === 'no-permissions' && 'border-border bg-warning-dim'
            )}
          >
            <Icon className={cn(iconSizeMap[iconSize], config.iconColor)} />
          </div>
        ) : null}
      </AnimatedContainer>

      {/* Title */}
      <AnimatedContainer direction="up" delay={0.05}>
        <h3 className={cn('font-semibold text-fg mb-2', sizeConfig.title)}>
          {finalTitle}
        </h3>
      </AnimatedContainer>

      {/* Description */}
      {finalDescription && (
        <AnimatedContainer direction="up" delay={0.1}>
          <p className={cn('text-fg-muted mb-6', sizeConfig.description)}>
            {finalDescription}
          </p>
        </AnimatedContainer>
      )}

      {/* Actions */}
      {(primaryAction || secondaryAction) && (
        <AnimatedContainer
          direction="up"
          delay={0.15}
          className="flex flex-col sm:flex-row items-center gap-3 mt-2"
        >
          {primaryAction && (
            <button
              onClick={primaryAction.onClick}
              className={cn(
                'inline-flex items-center justify-center gap-2',
                'rounded-button font-medium transition-all duration-150',
                'bg-primary text-bg-void hover:bg-primary-hover active:bg-primary-active',
                'focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-bg',
                'motion-reduce:transition-none',
                sizeConfig.button
              )}
            >
              {primaryAction.icon && <primaryAction.icon className="w-4 h-4" />}
              {primaryAction.label}
            </button>
          )}

          {secondaryAction && (
            <button
              onClick={secondaryAction.onClick}
              className={cn(
                'inline-flex items-center justify-center',
                'text-primary hover:text-primary-hover underline-offset-4 hover:underline',
                'transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-bg',
                'motion-reduce:transition-none',
                size === 'sm' ? 'text-sm' : 'text-base'
              )}
            >
              {secondaryAction.label}
            </button>
          )}
        </AnimatedContainer>
      )}
    </div>
  );
}

// Preset empty state components for common use cases
export const NoDataEmptyState = ({
  title,
  description,
  onCreateClick,
  createLabel,
  ...props
}: Partial<EmptyStateProps> & {
  onCreateClick?: () => void;
  createLabel?: string;
}) => {
  const { t } = useTranslation();
  return (
    <EmptyState
      type="no-data"
      title={title ?? t('emptyStates.noData.title', 'No data yet')}
      description={
        description ??
        t('emptyStates.noData.description', 'Get started by creating your first item.')
      }
      primaryAction={
        onCreateClick
          ? {
              label: createLabel ?? t('emptyStates.noData.create', 'Create New'),
              onClick: onCreateClick,
              icon: Plus,
            }
          : undefined
      }
      {...props}
    />
  );
};

export const NoResultsEmptyState = ({
  title,
  description,
  onClearFilters,
  ...props
}: Partial<EmptyStateProps> & {
  onClearFilters?: () => void;
}) => {
  const { t } = useTranslation();
  return (
    <EmptyState
      type="no-results"
      title={title ?? t('emptyStates.noResults.title', 'No results found')}
      description={
        description ??
        t('emptyStates.noResults.description', 'Try adjusting your search or filter.')
      }
      secondaryAction={
        onClearFilters
          ? {
              label: t('emptyStates.noResults.clearFilters', 'Clear filters'),
              onClick: onClearFilters,
            }
          : undefined
      }
      {...props}
    />
  );
};

export const NoPermissionsEmptyState = ({
  title,
  description,
  onRequestAccess,
  ...props
}: Partial<EmptyStateProps> & {
  onRequestAccess?: () => void;
}) => {
  const { t } = useTranslation();
  return (
    <EmptyState
      type="no-permissions"
      title={title ?? t('emptyStates.noPermissions.title', 'Access restricted')}
      description={
        description ??
        t(
          'emptyStates.noPermissions.description',
          "You don't have permission to view this content."
        )
      }
      primaryAction={
        onRequestAccess
          ? {
              label: t('emptyStates.noPermissions.requestAccess', 'Request Access'),
              onClick: onRequestAccess,
            }
          : undefined
      }
      {...props}
    />
  );
};

export const ErrorEmptyState = ({
  title,
  description,
  onRetry,
  ...props
}: Partial<EmptyStateProps> & {
  onRetry?: () => void;
}) => {
  const { t } = useTranslation();
  return (
    <EmptyState
      type="error"
      title={title ?? t('emptyStates.error.title', 'Something went wrong')}
      description={
        description ??
        t(
          'emptyStates.error.description',
          'An error occurred while loading this content.'
        )
      }
      primaryAction={
        onRetry
          ? {
              label: t('emptyStates.error.retry', 'Try Again'),
              onClick: onRetry,
            }
          : undefined
      }
      {...props}
    />
  );
};

// Specialized empty states for specific features
export const NoFilesEmptyState = ({
  onUploadClick,
  title,
  description,
  primaryAction,
  ...props
}: Partial<EmptyStateProps> & {
  onUploadClick?: () => void;
}) => {
  const { t } = useTranslation();
  return (
    <EmptyState
      icon={FileX}
      title={title ?? t('emptyStates.noFiles.title', 'No files uploaded')}
      description={
        description ??
        t(
          'emptyStates.noFiles.description',
          'Upload your first file to get started with analysis.'
        )
      }
      primaryAction={
        primaryAction ||
        (onUploadClick
          ? {
              label: t('emptyStates.noFiles.upload', 'Upload File'),
              onClick: onUploadClick,
              icon: Upload,
            }
          : undefined)
      }
      {...props}
    />
  );
};

export const NoProjectsEmptyState = ({
  onCreateClick,
  title,
  description,
  primaryAction,
  ...props
}: Partial<EmptyStateProps> & {
  onCreateClick?: () => void;
}) => {
  const { t } = useTranslation();
  return (
    <EmptyState
      icon={FolderOpen}
      title={title ?? t('emptyStates.noProjects.title', 'No projects yet')}
      description={
        description ??
        t(
          'emptyStates.noProjects.description',
          'Create your first project to organize your work.'
        )
      }
      primaryAction={
        primaryAction ||
        (onCreateClick
          ? {
              label: t('emptyStates.noProjects.create', 'Create Project'),
              onClick: onCreateClick,
              icon: Plus,
            }
          : undefined)
      }
      {...props}
    />
  );
};

export const NoTeamMembersEmptyState = ({
  onInviteClick,
  title,
  description,
  primaryAction,
  ...props
}: Partial<EmptyStateProps> & {
  onInviteClick?: () => void;
}) => {
  const { t } = useTranslation();
  return (
    <EmptyState
      icon={Users}
      title={title ?? t('emptyStates.noTeamMembers.title', 'No team members')}
      description={
        description ??
        t(
          'emptyStates.noTeamMembers.description',
          'Invite colleagues to collaborate on this project.'
        )
      }
      primaryAction={
        primaryAction ||
        (onInviteClick
          ? {
              label: t('emptyStates.noTeamMembers.invite', 'Invite Members'),
              onClick: onInviteClick,
              icon: Plus,
            }
          : undefined)
      }
      {...props}
    />
  );
};

// ============================================
// Educational Empty States (Phase 4 UX Redesign)
// ============================================

export interface EducationalEmptyStateProps {
  /** Icon to display */
  icon: LucideIcon;
  /** Main title */
  title: string;
  /** Subtitle/description */
  description: string;
  /** Feature list with bullet points */
  features: string[];
  /** Primary action button */
  primaryAction?: {
    label: string;
    onClick: () => void;
    icon?: LucideIcon;
  };
  /** Secondary action button */
  secondaryAction?: {
    label: string;
    onClick: () => void;
    icon?: LucideIcon;
  };
  /** Demo mode link */
  onDemoClick?: () => void;
  /** Demo link label */
  demoLabel?: string;
  /** Additional className */
  className?: string;
}

export function EducationalEmptyState({
  icon: Icon,
  title,
  description,
  features,
  primaryAction,
  secondaryAction,
  onDemoClick,
  demoLabel,
  className,
}: EducationalEmptyStateProps) {
  const { t } = useTranslation();
  const resolvedDemoLabel =
    demoLabel ?? t('emptyStates.educational.viewDemoData', 'View demo data');
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center text-center py-16 px-6',
        className
      )}
    >
      {/* Icon */}
      <div className="mb-6 p-4 rounded-2xl bg-primary-dim">
        <Icon className="w-12 h-12 text-primary" />
      </div>

      {/* Title */}
      <h3 className="text-base font-medium text-fg mb-2">{title}</h3>

      {/* Description */}
      <p className="text-fg-muted mb-6 max-w-md">{description}</p>

      {/* Features list */}
      <ul className="text-left mb-8 space-y-2 max-w-sm">
        {features.map((feature) => (
          <li key={feature} className="flex items-start gap-2 text-fg-muted">
            <span className="text-primary mt-1">•</span>
            <span>{feature}</span>
          </li>
        ))}
      </ul>

      {/* Action buttons */}
      <div className="flex flex-col sm:flex-row items-center gap-3">
        {primaryAction && (
          <Button
            onClick={primaryAction.onClick}
            size="lg"
            // Hero call-to-action: 48px, taller than the lg step (40px) — pre-existing, no variant for it yet.
            className="min-h-12 px-6 gap-2"
          >
            {primaryAction.icon && <primaryAction.icon className="w-5 h-5" />}
            {primaryAction.label}
          </Button>
        )}
        {secondaryAction && (
          <Button
            variant="outline"
            onClick={secondaryAction.onClick}
            size="lg"
            // Hero call-to-action: 48px, taller than the lg step (40px) — pre-existing, no variant for it yet.
            className="min-h-12 px-6 gap-2"
          >
            {secondaryAction.icon && <secondaryAction.icon className="w-5 h-5" />}
            {secondaryAction.label}
          </Button>
        )}
      </div>

      {/* Demo link */}
      {onDemoClick && (
        <button
          onClick={onDemoClick}
          className="mt-6 inline-flex items-center gap-2 text-sm text-primary hover:text-primary-hover transition-colors"
        >
          <Lightbulb className="w-4 h-4" />
          {resolvedDemoLabel}
        </button>
      )}
    </div>
  );
}

// Records (Conversations) Educational Empty State
export interface RecordsEducationalEmptyStateProps {
  onConnectBitrix24?: () => void;
  onUploadFile?: () => void;
  onDemoClick?: () => void;
  /** i18n props */
  title?: string;
  description?: string;
  features?: string[];
  connectLabel?: string;
  uploadLabel?: string;
  demoLabel?: string;
}

export function RecordsEducationalEmptyState({
  onConnectBitrix24,
  onUploadFile,
  onDemoClick,
  title,
  description,
  features,
  connectLabel,
  uploadLabel,
  demoLabel,
}: RecordsEducationalEmptyStateProps) {
  const { t } = useTranslation();
  return (
    <EducationalEmptyState
      icon={MessageSquare}
      title={
        title ?? t('emptyStates.records.title', 'Your conversations will appear here')
      }
      description={
        description ??
        t(
          'emptyStates.records.description',
          'Once you connect a data source, you will see:'
        )
      }
      features={
        features || [
          t(
            'emptyStates.records.features.list',
            'List of all calls, chats, and meetings with filters'
          ),
          t('emptyStates.records.features.transcription', 'Automatic transcription'),
          t(
            'emptyStates.records.features.quality',
            'Quality score for each conversation'
          ),
        ]
      }
      primaryAction={
        onConnectBitrix24
          ? {
              label:
                connectLabel ??
                t('emptyStates.records.actions.connectBitrix24', 'Connect Bitrix24'),
              onClick: onConnectBitrix24,
              icon: Cloud,
            }
          : undefined
      }
      secondaryAction={
        onUploadFile
          ? {
              label:
                uploadLabel ?? t('emptyStates.records.actions.uploadFile', 'Upload File'),
              onClick: onUploadFile,
              icon: Upload,
            }
          : undefined
      }
      onDemoClick={onDemoClick}
      demoLabel={demoLabel ?? t('emptyStates.educational.viewDemoData', 'View demo data')}
    />
  );
}

// Alerts (Issues) Educational Empty State
export interface AlertsEducationalEmptyStateProps {
  onConfigureAlerts?: () => void;
  onDemoClick?: () => void;
  /** i18n props */
  title?: string;
  description?: string;
  features?: string[];
  configureLabel?: string;
  demoLabel?: string;
}

export function AlertsEducationalEmptyState({
  onConfigureAlerts,
  onDemoClick,
  title,
  description,
  features,
  configureLabel,
  demoLabel,
}: AlertsEducationalEmptyStateProps) {
  const { t } = useTranslation();
  return (
    <EducationalEmptyState
      icon={Bell}
      title={title ?? t('emptyStates.alerts.title', 'No issues requiring attention')}
      description={
        description ?? t('emptyStates.alerts.description', 'Aelo will notify you when:')
      }
      features={
        features || [
          t('emptyStates.alerts.features.quality', 'Quality drops below threshold'),
          t(
            'emptyStates.alerts.features.scriptCompliance',
            'Script compliance issues detected'
          ),
          t(
            'emptyStates.alerts.features.repeatedProblems',
            'Repeated problems with same manager'
          ),
        ]
      }
      primaryAction={
        onConfigureAlerts
          ? {
              label:
                configureLabel ??
                t('emptyStates.alerts.actions.configureRules', 'Configure alert rules'),
              onClick: onConfigureAlerts,
            }
          : undefined
      }
      onDemoClick={onDemoClick}
      demoLabel={
        demoLabel ?? t('emptyStates.alerts.actions.viewSample', 'View sample alerts')
      }
    />
  );
}
